import { useEffect, useState } from "react";
import api, { fmtDateTime, formatApiError } from "@/lib/apiClient";
import { logoSrc } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Download, Pencil } from "lucide-react";
import { FieldInput } from "@/components/forms/FieldInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const fmt = (f, v) => {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return <span className="text-zinc-400">—</span>;
  if (f.type === "pass_fail") return <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${v === "fail" ? "bg-rose-100 text-rose-700" : v === "pass" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{v === "na" ? "N/A" : v}</span>;
  if (f.type === "yes_no") return v === "yes" ? "Yes" : "No";
  if (f.type === "checkboxes") return v.join(", ");
  if (f.type === "site") return v?.name || "—";
  if (f.type === "photo") return <div className="flex flex-wrap gap-2">{v.map((u) => <a key={u} href={logoSrc(u)} target="_blank" rel="noreferrer"><img src={logoSrc(u)} alt="" className="h-20 w-20 object-cover rounded border" /></a>)}</div>;
  if (f.type === "signature") return <img src={v} alt="signature" className="h-14 bg-white border rounded" />;
  return <span className="whitespace-pre-line">{String(v)}</span>;
};

export async function downloadSubmissionPdf(s) {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const win = ios ? window.open("", "_blank") : null;
  try {
    const res = await api.get(`/form-submissions/${s.id}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    if (win) { win.location.href = url; } else {
      const a = document.createElement("a");
      a.href = url; a.download = `${s.form_title || "form"}-${s.employee_name || ""}-${(s.submitted_at || "").slice(0, 10)}.pdf`.replace(/[\\/:*?"<>|]/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    let msg = "Could not download PDF";
    try { const t = JSON.parse(await e.response?.data?.text()); if (t?.detail) msg = t.detail; } catch { /* keep default */ }
    toast.error(msg);
  }
}

export function SubmissionView({ id, onClose, onChanged, canResolve }) {
  const [s, setS] = useState(null);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState(null);
  const [why, setWhy] = useState("");
  const saveEdit = async () => {
    try { const { data } = await api.put(`/form-submissions/${id}`, { answers: edit, reason: why }); setS(data); setEdit(null); setWhy(""); toast.success("Submission updated"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { setEdit(null); setWhy(""); }, [id]);
  useEffect(() => { setS(null); if (id) api.get(`/form-submissions/${id}`).then((r) => setS(r.data)).catch(() => toast.error("Submission not found")); }, [id]);
  const resolve = async () => {
    try { const { data } = await api.post(`/form-submissions/${id}/resolve`, { note }); setS(data); toast.success("Marked as resolved"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submission-view">
        <DialogHeader>
          <DialogTitle>{s?.form_title || "Submission"}</DialogTitle>
          {s && <DialogDescription>{s.employee_name} · {fmtDateTime(s.submitted_at)}{s.site_name ? ` · ${s.site_name}` : ""}</DialogDescription>}
        </DialogHeader>
        {s && (
          <div className="space-y-3">
            {s.has_fail && (
              <div data-testid="submission-fail-banner" className={`rounded-lg p-3 text-sm flex gap-2 ${s.resolved ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
                {s.resolved ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                <div>{s.resolved ? <>Resolved by {s.resolved_by}{s.resolution_note && `: ${s.resolution_note}`}</> : <>Failed: {s.fail_items.join(", ")}</>}</div>
              </div>
            )}
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-lg">
              {s.fields.map((f) => f.type === "heading"
                ? <div key={f.id} className="px-3 py-2 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">{f.label}</div>
                : <div key={f.id} className="px-3 py-2.5 grid sm:grid-cols-[1fr_1.3fr] gap-1 text-sm"><div className="text-zinc-600">{f.label}</div><div>{edit ? <FieldInput field={f} value={edit[f.id]} onChange={(v) => setEdit((p) => ({ ...p, [f.id]: v }))} /> : fmt(f, s.answers[f.id])}</div></div>)}
            </div>
            {s.edited_at && !edit && <div data-testid="submission-edited" className="text-xs text-zinc-500">Edited by {s.edited_by} on {fmtDateTime(s.edited_at)}{s.edit_history?.length ? ` · ${s.edit_history.length} correction${s.edit_history.length === 1 ? "" : "s"}: ${s.edit_history.map((h) => h.reason).join("; ")}` : ""}</div>}
            {canResolve && !edit && <Button data-testid="submission-edit-btn" variant="outline" onClick={() => setEdit({ ...(s.answers || {}) })}><Pencil className="h-4 w-4 mr-1" />Edit submission</Button>}
            {edit && (
              <div className="space-y-2 border border-amber-200 bg-amber-50 rounded-lg p-3">
                <Label>What was corrected? (required)</Label>
                <Textarea data-testid="submission-edit-reason" rows={2} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. Wrong delivery address" />
                <div className="flex gap-2"><Button data-testid="submission-edit-save" disabled={!why.trim()} onClick={saveEdit} className="bg-slate-900 text-white">Save changes</Button><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
              </div>
            )}
            {canResolve && s.has_fail && !s.resolved && (
              <div className="space-y-2">
                <Textarea data-testid="resolve-note" rows={2} placeholder="What was done? e.g. Tyre replaced 12/05" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button data-testid="resolve-btn" variant="outline" onClick={resolve}>Mark as resolved</Button>
              </div>
            )}
            <Button data-testid="submission-pdf" onClick={() => downloadSubmissionPdf(s)} className="w-full bg-slate-900 text-white"><Download className="h-4 w-4 mr-1.5" />Download branded PDF</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
